import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Form, Button, Select, Input, InputNumber, Checkbox, notification } from 'antd'
import { ArrowLeftOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { motion } from 'framer-motion'
import theme from '../../theme'
import {
  useReturnEligibility,
  EligibleItem,
  calculateReturnDaysRemaining,
  getReturnWindowUrgency,
  getReturnWindowUrgencyColor,
  ReturnWindowUrgency,
} from '../../hooks/useReturnEligibility'
import { useReturnReasons } from '../../hooks/useReturnReasons'
import { useSubmitReturn } from '../../hooks/useSubmitReturn'
import { useTracking } from '../../hooks/useTracking'
import { useAnalytics } from '../../hooks/useAnalytics'

const FormPanel = styled(motion.div)`
  background: #fff;
  border-radius: 6px;
  padding: 20px 19px 24px 19px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  min-height: 300px;
`

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${theme.cornFlower500};
  font-size: 14px;
  line-height: 21px;
  margin-bottom: 12px;

  &:hover {
    opacity: 0.8;
  }
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.midnight800};
  margin: 0 0 4px 0;
`

const Subtitle = styled.p`
  font-size: 13px;
  color: ${theme.slate500};
  margin: 0 0 20px 0;
`

const SectionLabel = styled.div`
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 500;
  color: ${theme.midnight500};
  margin-bottom: 10px;
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${theme.slate100};
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 20px;
`

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 14px;
  gap: 12px;
  border-bottom: 1px solid ${theme.slate100};

  &:last-child {
    border-bottom: none;
  }
`

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemDescription = styled.div`
  font-size: 14px;
  color: ${theme.midnight800};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ItemMeta = styled.div`
  font-size: 12px;
  color: ${theme.slate500};
  margin-top: 2px;
`

const ItemPrice = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.midnight800};
  white-space: nowrap;
`

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: ${theme.slate500};
`

const ReturnWindow = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  font-size: 12px;
  color: ${theme.slate500};
  margin: 16px 0;
`

const ReturnWindowIcon = styled(ClockCircleOutlined)<{
  $urgency: ReturnWindowUrgency
}>`
  color: ${({ $urgency }) => getReturnWindowUrgencyColor($urgency)};
  font-size: 13px;
`

type SelectedItem = {
  sku: string
  quantity: number
  maxQuantity: number
}

// Sums (quantity × sellingPrice) across selected items. Items missing a
// sellingPrice contribute 0 — the API may omit it when product-level data is
// unavailable for the order, in which case the FE-computed value will undercount.
export function computeReturnValue(
  selected: { sku: string; quantity: number }[],
  items: EligibleItem[]
): number {
  const priceBySku = new Map(items.map((i) => [i.sku, i.sellingPrice ?? 0]))
  return selected.reduce(
    (sum, s) => sum + s.quantity * (priceBySku.get(s.sku) ?? 0),
    0
  )
}

type Props = {
  onBack: () => void
  onSuccess: (returnRequestId: string) => void
}

export default function ReturnRequestForm({ onBack, onSuccess }: Props) {
  const {
    items,
    reasons: eligibilityReasons,
    returnWindowClosesAt,
    accountId,
  } = useReturnEligibility()
  // Fallback: fetch from dedicated endpoint if eligibility response didn't include reasons
  const { data: reasonsData } = useReturnReasons(eligibilityReasons.length === 0)
  const reasons = eligibilityReasons.length > 0
    ? eligibilityReasons
    : reasonsData?.reasons ?? []
  const { orderDetails } = useTracking()
  const { track } = useAnalytics()
  const [form] = Form.useForm()
  const isDirtyRef = useRef(false)
  const submittedRef = useRef(false)

  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])

  const { mutate: submitReturn, isLoading: isSubmitting } = useSubmitReturn({
    onSuccess: (data) => {
      submittedRef.current = true
      const reasonCode = form.getFieldValue('reason_code') as string | undefined
      const subReasonCode = form.getFieldValue('sub_reason_code') as string | undefined
      track('returns.request.submitted', {
        action: 'Submit',
        category: 'Returns',
        product: 'TrackOrder',
        orderId: String(orderDetails?.id),
        accountId,
        reasonCode: [reasonCode, subReasonCode].filter(Boolean).join(' > '),
        itemCount: selectedItems.length,
        returnValue: computeReturnValue(selectedItems, items),
      })
      onSuccess(data.id)
    },
    onError: () => {
      notification.error({
        message: 'Failed to submit return request',
        description: 'Please try again. If the problem persists, contact support.',
        duration: 10,
      })
    },
  })

  const handleItemToggle = useCallback(
    (item: EligibleItem, checked: boolean) => {
      isDirtyRef.current = true
      setSelectedItems((prev) => {
        if (checked) {
          return [...prev, { sku: item.sku, quantity: item.quantity, maxQuantity: item.quantity }]
        }
        return prev.filter((s) => s.sku !== item.sku)
      })
    },
    []
  )

  const handleQuantityChange = useCallback(
    (sku: string, quantity: number | null) => {
      if (quantity === null) return
      isDirtyRef.current = true
      setSelectedItems((prev) =>
        prev.map((s) => (s.sku === sku ? { ...s, quantity } : s))
      )
    },
    []
  )

  // Fire `returns.request.abandoned` on form unmount when the user has touched
  // the form but didn't submit. Covers Back-button, browser back, and the form
  // being unmounted by the parent for any other reason.
  // We capture the firing logic in a ref kept current on each render so the
  // effect can have an empty dep array — this avoids the cleanup running on
  // every dep change (which would mis-fire abandons while the user is still
  // interacting).
  const fireAbandonRef = useRef(() => {})
  fireAbandonRef.current = () => {
    if (!isDirtyRef.current || submittedRef.current) return
    track('returns.request.abandoned', {
      action: 'NavigateAway',
      category: 'Returns',
      product: 'TrackOrder',
      orderId: String(orderDetails?.id),
      accountId,
    })
  }
  useEffect(() => {
    return () => fireAbandonRef.current()
  }, [])

  const handleSubmit = (values: {
    reason_code: string
    sub_reason_code?: string
    customer_notes?: string
  }) => {
    if (selectedItems.length === 0) return
    submitReturn({
      items: selectedItems.map(({ sku, quantity }) => ({ sku, quantity })),
      reason_code: values.reason_code,
      sub_reason_code: values.sub_reason_code || undefined,
      customer_notes: values.customer_notes || undefined,
    })
  }

  const selectedReasonCode = Form.useWatch('reason_code', form)
  const selectedReason = reasons.find((r) => r.code === selectedReasonCode)
  const hasSubReasons = (selectedReason?.subReasons?.length ?? 0) > 0

  const daysRemaining = calculateReturnDaysRemaining(returnWindowClosesAt)
  const urgency =
    daysRemaining !== null ? getReturnWindowUrgency(daysRemaining) : 'normal'

  const partnerName = orderDetails?.partner?.name

  return (
    <FormPanel
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      data-testid="return-request-form"
    >
      <BackLink onClick={onBack} data-testid="back-to-tracking">
        <ArrowLeftOutlined /> Back to tracking
      </BackLink>

      <Title>Request a Return</Title>
      <Subtitle>
        Order {orderDetails?.partnerOrderId || orderDetails?.uuid}
        {partnerName ? ` \u00B7 ${partnerName}` : ''}
      </Subtitle>

      <Form
        form={form}
        layout="vertical"
        size="large"
        onFinish={handleSubmit}
        onValuesChange={() => {
          isDirtyRef.current = true
        }}
      >
        <SectionLabel>Select items to return</SectionLabel>
        <ItemList>
          {items.map((item) => {
            const selected = selectedItems.find((s) => s.sku === item.sku)
            return (
              <ItemRow key={item.sku}>
                <Checkbox
                  checked={!!selected}
                  onChange={(e) => handleItemToggle(item, e.target.checked)}
                  data-testid={`item-checkbox-${item.sku}`}
                />
                <ItemDetails>
                  <ItemDescription>{item.description}</ItemDescription>
                  <ItemMeta>Qty {item.quantity}</ItemMeta>
                  {selected && item.quantity > 1 && (
                    <QuantityRow>
                      Return qty:
                      <InputNumber
                        min={1}
                        max={item.quantity}
                        value={selected.quantity}
                        onChange={(val) => handleQuantityChange(item.sku, val)}
                        size="small"
                        style={{ width: 60 }}
                      />
                    </QuantityRow>
                  )}
                </ItemDetails>
                {item.sellingPrice != null && (
                  <ItemPrice>{item.sellingPrice} AED</ItemPrice>
                )}
              </ItemRow>
            )
          })}
        </ItemList>

        <Form.Item
          name="reason_code"
          label="Reason for return"
          rules={[{ required: true, message: 'Please select a reason' }]}
        >
          <Select
            placeholder="Select a reason"
            onChange={() => form.setFieldValue('sub_reason_code', undefined)}
            options={reasons.map((r) => ({
              label: r.labelCustomer,
              value: r.code,
            }))}
          />
        </Form.Item>

        {hasSubReasons && (
          <Form.Item
            name="sub_reason_code"
            label="More details"
          >
            <Select
              placeholder="Select a sub-reason (optional)"
              allowClear
              options={selectedReason!.subReasons.map((sub) => ({
                label: sub.labelCustomer,
                value: sub.code,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item name="customer_notes" label="Additional details">
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="Tell us more about why you're returning (optional)"
          />
        </Form.Item>

        {daysRemaining !== null && (
          <ReturnWindow data-urgency={urgency}>
            <ReturnWindowIcon $urgency={urgency} />
            Return window: {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}{' '}
            remaining
          </ReturnWindow>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isSubmitting}
            disabled={selectedItems.length === 0}
            data-testid="submit-return-button"
          >
            Submit Return Request
          </Button>
        </Form.Item>
      </Form>
    </FormPanel>
  )
}
